import { useState, useEffect } from 'react';
import {
  Loader2,
  Save,
  Settings,
  FileText,
  Image,
  Mail,
  Send,
  Upload,
  X,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { UserRole } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  usePropertySettings,
  useUpdatePropertySettings,
  useGuestEngagement,
  useUpdateGuestEngagement,
} from '@/hooks/api/useAdmin';
import {
  useEmailConfig,
  useSaveEmailConfig,
  useTestEmailConfig,
} from '@/hooks/api/useNotifications';
import type {
  UpdatePropertySettingsPayload,
  GuestEngagementTemplate,
  GuestEngagementType,
  UpdateGuestEngagementPayload,
} from '@/services/api/admin.api';
import type { UpdateEmailConfigPayload } from '@/services/api/notifications.api';

// ─── Constants ───────────────────────────────────────────────────────────────

type SettingsTab = 'basic' | 'policies' | 'images' | 'engagement' | 'email';

const TABS: { key: SettingsTab; label: string; icon: typeof Settings }[] = [
  { key: 'basic', label: 'Basic Data', icon: Settings },
  { key: 'policies', label: 'Policies', icon: FileText },
  { key: 'images', label: 'Images', icon: Image },
  { key: 'engagement', label: 'Guest Engagement', icon: Mail },
  { key: 'email', label: 'Email', icon: Send },
];

const POLICY_FIELDS = [
  { key: 'petPolicy', label: 'Pet Policy' },
  { key: 'cancellationPolicy', label: 'Cancellation Policy' },
  { key: 'houseRules', label: 'House Rules' },
  { key: 'smokingPolicy', label: 'Smoking Policy' },
] as const;

const EXTRA_POLICY_FIELDS = [
  'Refund Policy',
  'Guarantee Policy',
  'Reservation Hold Policy',
  'Advance Payments Policy',
  'Channel Reservation Policy',
  'Confirmation Thank You',
  'Cancellation Thank You',
  'Invoice Thank You',
] as const;

const ENGAGEMENT_TYPES: { type: GuestEngagementType; label: string }[] = [
  { type: 'confirmation', label: 'Booking Confirmation' },
  { type: 'cancellation', label: 'Cancellation Notice' },
  { type: 'arrival', label: 'Arrival Instructions' },
  { type: 'departure', label: 'Departure Information' },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function PropertySettingsPage() {
  const { user } = useAuth();
  const canAccess = user?.role === UserRole.ADMIN;
  const [activeTab, setActiveTab] = useState<SettingsTab>('basic');
  const propertyId = useSelectedPropertyId();

  if (!canAccess) {
    return <AccessDenied message="Property settings are restricted to Admins." />;
  }

  return (
    <div>
      <PageHeader
        title="Property Settings"
        description="Configure property details, policies, and guest communications"
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" role="tablist" aria-label="Settings sections">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                <Icon size={16} aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {!propertyId ? (
        <EmptyState icon={Settings} title="No property selected" description="Please create a property first." />
      ) : (
        <>
          {activeTab === 'basic' && <BasicDataTab propertyId={propertyId} />}
          {activeTab === 'policies' && <PoliciesTab propertyId={propertyId} />}
          {activeTab === 'images' && <ImagesTab propertyId={propertyId} />}
          {activeTab === 'engagement' && <GuestEngagementTab propertyId={propertyId} />}
          {activeTab === 'email' && <EmailConfigTab propertyId={propertyId} />}
        </>
      )}
    </div>
  );
}

// ─── Basic Data Tab ──────────────────────────────────────────────────────────

function BasicDataTab({ propertyId }: { propertyId: string }) {
  const { data: settings, isLoading } = usePropertySettings(propertyId);
  const updateMutation = useUpdatePropertySettings();

  const [form, setForm] = useState<UpdatePropertySettingsPayload>({});
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    if (settings) {
      setForm({
        name: settings.name,
        tagLine: settings.tagLine ?? undefined,
        phone: settings.phone ?? undefined,
        email: settings.email ?? undefined,
        fax: settings.fax ?? undefined,
        website: settings.website ?? undefined,
        address: settings.address ?? undefined,
        city: settings.city ?? undefined,
        state: settings.state ?? undefined,
        country: settings.country ?? undefined,
        zipCode: settings.zipCode ?? undefined,
        checkInTime: settings.checkInTime ?? undefined,
        checkOutTime: settings.checkOutTime ?? undefined,
        logoUrl: settings.logoUrl ?? undefined,
        roomTaxRate: settings.roomTaxRate !== undefined ? Number(settings.roomTaxRate) : undefined,
        foodTaxRate: settings.foodTaxRate !== undefined ? Number(settings.foodTaxRate) : undefined,
      });
      setLogoPreview(settings.logoUrl ?? '');
    }
  }, [settings]);

  if (isLoading) return <LoadingSpinner />;

  const handleSave = () => {
    updateMutation.mutate({ propertyId, payload: form });
  };

  const updateField = (field: keyof UpdatePropertySettingsPayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setLogoPreview(dataUrl);
      updateField('logoUrl', dataUrl);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Property Name" value={form.name ?? ''} onChange={(v) => updateField('name', v)} />
          <FormField label="Tag Line" value={form.tagLine ?? ''} onChange={(v) => updateField('tagLine', v)} />
          <FormField label="Phone" value={form.phone ?? ''} onChange={(v) => updateField('phone', v)} />
          <FormField label="Email" value={form.email ?? ''} onChange={(v) => updateField('email', v)} type="email" />
          <FormField label="Fax" value={form.fax ?? ''} onChange={(v) => updateField('fax', v)} />
          <FormField label="Website" value={form.website ?? ''} onChange={(v) => updateField('website', v)} type="url" />
        </div>
        <hr className="border-gray-100" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Address" value={form.address ?? ''} onChange={(v) => updateField('address', v)} />
          <FormField label="City" value={form.city ?? ''} onChange={(v) => updateField('city', v)} />
          <FormField label="State" value={form.state ?? ''} onChange={(v) => updateField('state', v)} />
          <FormField label="Country" value={form.country ?? ''} onChange={(v) => updateField('country', v)} />
          <FormField label="ZIP Code" value={form.zipCode ?? ''} onChange={(v) => updateField('zipCode', v)} />
        </div>
        <hr className="border-gray-100" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Check-In Time" value={form.checkInTime ?? ''} onChange={(v) => updateField('checkInTime', v)} type="time" />
          <FormField label="Check-Out Time" value={form.checkOutTime ?? ''} onChange={(v) => updateField('checkOutTime', v)} type="time" />
        </div>

        {/* Taxes — per-section tax % on the folio (Room vs Food bills). Applied
            automatically to each section's subtotal and rolled into the Master
            Bill total. Leave 0 for no tax. */}
        <hr className="border-gray-100" />
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-1">Taxes</p>
          <p className="text-xs text-gray-400 mb-3">
            Applied automatically to each section of the guest bill. E.g. Room 12%, Food 5%. Set 0 for no tax.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TaxRateField
              label="Room tax %"
              value={form.roomTaxRate}
              onChange={(n) => setForm((prev) => ({ ...prev, roomTaxRate: n }))}
            />
            <TaxRateField
              label="Food tax %"
              value={form.foodTaxRate}
              onChange={(n) => setForm((prev) => ({ ...prev, foodTaxRate: n }))}
            />
          </div>
        </div>

        {/* Logo upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Property Logo</label>
          {logoPreview ? (
            <div className="relative w-32 h-32 rounded-lg border border-gray-200 overflow-hidden mb-3">
              <img src={logoPreview} alt="Property logo" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => {
                  setLogoPreview('');
                  updateField('logoUrl', '');
                }}
                className="absolute top-1 right-1 p-1 bg-white/80 rounded-full text-gray-500 hover:text-red-500"
                aria-label="Remove logo"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors">
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoUpload} />
            <div className="text-center">
              <Upload size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Click to upload logo</p>
              <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}

// ─── Policies Tab ────────────────────────────────────────────────────────────

function PoliciesTab({ propertyId }: { propertyId: string }) {
  const { data: settings, isLoading } = usePropertySettings(propertyId);
  const updateMutation = useUpdatePropertySettings();

  const [policyForm, setPolicyForm] = useState<Record<string, string>>({});
  const [extraPolicies, setExtraPolicies] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings) {
      const pf: Record<string, string> = {};
      for (const f of POLICY_FIELDS) {
        pf[f.key] = (settings as unknown as Record<string, string | null>)[f.key] ?? '';
      }
      setPolicyForm(pf);
      setExtraPolicies(settings.policies ?? {});
    }
  }, [settings]);

  if (isLoading) return <LoadingSpinner />;

  const handleSave = () => {
    const payload: UpdatePropertySettingsPayload = {
      ...policyForm as Pick<UpdatePropertySettingsPayload, 'petPolicy' | 'cancellationPolicy' | 'houseRules' | 'smokingPolicy'>,
      policies: extraPolicies,
    };
    updateMutation.mutate({ propertyId, payload });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
      <div className="space-y-5">
        {POLICY_FIELDS.map((f) => (
          <div key={f.key}>
            <label htmlFor={`policy-${f.key}`} className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
            <textarea
              id={`policy-${f.key}`}
              rows={3}
              value={policyForm[f.key] ?? ''}
              onChange={(e) => setPolicyForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        ))}

        <hr className="border-gray-100" />
        <h3 className="text-sm font-semibold text-gray-700">Additional Policies</h3>

        {EXTRA_POLICY_FIELDS.map((label) => {
          const key = label.toLowerCase().replace(/\s+/g, '_');
          return (
            <div key={key}>
              <label htmlFor={`extra-${key}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <textarea
                id={`extra-${key}`}
                rows={3}
                value={extraPolicies[key] ?? ''}
                onChange={(e) => setExtraPolicies((prev) => ({ ...prev, [key]: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save Policies
        </Button>
      </div>
    </div>
  );
}

// ─── Images Tab ──────────────────────────────────────────────────────────────

function ImagesTab({ propertyId }: { propertyId: string }) {
  const { data: settings, isLoading } = usePropertySettings(propertyId);
  const updateMutation = useUpdatePropertySettings();
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (settings) {
      setImages(settings.images ?? []);
    }
  }, [settings]);

  if (isLoading) return <LoadingSpinner />;

  const handleImagesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (file.size > 2 * 1024 * 1024) {
        alert(`${file.name} exceeds 2MB limit and was skipped`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setImages((prev) => {
          const next = [...prev, dataUrl];
          return next;
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    updateMutation.mutate({ propertyId, payload: { images } });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Property Images</label>
        {images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-video rounded-lg border border-gray-200 overflow-hidden">
                <img src={img} alt={`Property image ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 p-1 bg-white/80 rounded-full text-gray-500 hover:text-red-500"
                  aria-label={`Remove image ${i + 1}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors">
          <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={handleImagesUpload} />
          <div className="text-center">
            <Upload size={24} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">Click to upload images</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB each</p>
          </div>
        </label>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save Images
        </Button>
      </div>
    </div>
  );
}

// ─── Guest Engagement Tab ────────────────────────────────────────────────────

function GuestEngagementTab({ propertyId }: { propertyId: string }) {
  const { data: templates, isLoading } = useGuestEngagement(propertyId);
  const [editingType, setEditingType] = useState<GuestEngagementType | null>(null);

  if (isLoading) return <LoadingSpinner />;

  const templateMap = new Map<GuestEngagementType, GuestEngagementTemplate>();
  if (templates) {
    for (const t of templates) {
      templateMap.set(t.templateType, t);
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      {ENGAGEMENT_TYPES.map(({ type, label }) => {
        const template = templateMap.get(type);
        const isEditing = editingType === type;

        return (
          <div key={type} className="bg-white rounded-xl border border-gray-200">
            <button
              type="button"
              onClick={() => setEditingType(isEditing ? null : type)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
              aria-expanded={isEditing}
            >
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-gray-400 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {template?.isEnabled ? 'Enabled' : 'Disabled'}
                    {template?.subject ? ` - ${template.subject}` : ''}
                  </p>
                </div>
              </div>
              <span className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                template?.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
              )}>
                {template?.isEnabled ? 'Active' : 'Inactive'}
              </span>
            </button>

            {isEditing && (
              <EngagementEditor
                propertyId={propertyId}
                type={type}
                template={template ?? null}
                onClose={() => setEditingType(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function EngagementEditor({
  propertyId,
  type,
  template,
  onClose,
}: {
  propertyId: string;
  type: GuestEngagementType;
  template: GuestEngagementTemplate | null;
  onClose: () => void;
}) {
  const updateMutation = useUpdateGuestEngagement();
  const [isEnabled, setIsEnabled] = useState(template?.isEnabled ?? false);
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [propertyEmail, setPropertyEmail] = useState(template?.propertyEmail ?? '');

  const handleSave = () => {
    const payload: UpdateGuestEngagementPayload = {
      isEnabled,
      subject: subject || undefined,
      body: body || undefined,
      propertyEmail: propertyEmail || undefined,
    };
    updateMutation.mutate(
      { propertyId, type, payload },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
      <div className="flex items-center gap-3">
        <label htmlFor={`eng-enabled-${type}`} className="text-sm font-medium text-gray-700">Enabled</label>
        <input
          id={`eng-enabled-${type}`}
          type="checkbox"
          checked={isEnabled}
          onChange={(e) => setIsEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      </div>
      <FormField label="Property Email" value={propertyEmail} onChange={setPropertyEmail} type="email" />
      <FormField label="Subject" value={subject} onChange={setSubject} />
      <div>
        <label htmlFor={`eng-body-${type}`} className="block text-sm font-medium text-gray-700 mb-1">Body</label>
        <textarea
          id={`eng-body-${type}`}
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={handleSave} isLoading={updateMutation.isPending}>
          <Save size={14} aria-hidden="true" />
          Save
        </Button>
      </div>
    </div>
  );
}

// ─── Email Config Tab ────────────────────────────────────────────────────────

function EmailConfigTab({ propertyId }: { propertyId: string }) {
  const { data: config, isLoading } = useEmailConfig(propertyId);
  const saveMutation = useSaveEmailConfig(propertyId);
  const testMutation = useTestEmailConfig(propertyId);

  const [provider, setProvider] = useState<'sendgrid' | 'smtp'>('sendgrid');
  const [apiKey, setApiKey] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (config) {
      setProvider(config.provider);
      setSenderEmail(config.senderEmail ?? '');
      setSenderName(config.senderName ?? '');
      setIsActive(config.isActive);
      setSmtpHost(config.smtpHost ?? '');
      setSmtpPort(config.smtpPort?.toString() ?? '587');
    }
  }, [config]);

  if (isLoading) return <LoadingSpinner />;

  const handleSave = () => {
    const payload: UpdateEmailConfigPayload = {
      provider,
      senderEmail,
      senderName,
      ...(provider === 'sendgrid' ? { apiKey: apiKey || undefined } : {}),
      ...(provider === 'smtp'
        ? {
            smtpHost: smtpHost || undefined,
            smtpPort: smtpPort ? Number(smtpPort) : undefined,
            smtpUser: smtpUser || undefined,
            smtpPass: smtpPass || undefined,
          }
        : {}),
    };
    saveMutation.mutate(payload);
  };

  const handleTest = () => {
    testMutation.mutate(undefined);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
      <div className="space-y-5">
        {/* Active toggle */}
        <div className="flex items-center gap-3">
          <label htmlFor="email-active" className="text-sm font-medium text-gray-700">
            Active
          </label>
          <input
            id="email-active"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-xs text-gray-500">
            {isActive ? 'Email sending is enabled' : 'Email sending is disabled'}
          </span>
        </div>

        {/* Provider select */}
        <div>
          <label htmlFor="email-provider" className="block text-sm font-medium text-gray-700 mb-1">
            Provider
          </label>
          <select
            id="email-provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value as 'sendgrid' | 'smtp')}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="sendgrid">SendGrid</option>
            <option value="smtp">SMTP</option>
          </select>
        </div>

        {/* SendGrid fields */}
        {provider === 'sendgrid' && (
          <FormField
            label="API Key"
            value={apiKey}
            onChange={setApiKey}
            type="password"
          />
        )}

        {/* SMTP fields */}
        {provider === 'smtp' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="SMTP Host" value={smtpHost} onChange={setSmtpHost} />
            <FormField label="SMTP Port" value={smtpPort} onChange={setSmtpPort} />
            <FormField label="SMTP User" value={smtpUser} onChange={setSmtpUser} />
            <FormField label="SMTP Password" value={smtpPass} onChange={setSmtpPass} type="password" />
          </div>
        )}

        <hr className="border-gray-100" />

        {/* Sender fields (always shown) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Sender Email" value={senderEmail} onChange={setSenderEmail} type="email" />
          <FormField label="Sender Name" value={senderName} onChange={setSenderName} />
        </div>

        {/* Test result message */}
        {testMutation.isSuccess && (
          <div
            className={cn(
              'flex items-center gap-2 px-4 py-3 rounded-lg text-sm',
              testMutation.data?.success
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700',
            )}
            role="alert"
          >
            <Zap size={16} aria-hidden="true" />
            {testMutation.data?.message ?? 'Test completed.'}
          </div>
        )}
        {testMutation.isError && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700" role="alert">
            <Zap size={16} aria-hidden="true" />
            Failed to send test email. Please check your configuration.
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={handleTest} isLoading={testMutation.isPending}>
          <Send size={16} aria-hidden="true" />
          Send Test Email
        </Button>
        <Button onClick={handleSave} isLoading={saveMutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save Configuration
        </Button>
      </div>
    </div>
  );
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

function FormField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  const id = `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  );
}

/** Numeric percentage input (0–100) for a per-section tax rate. */
function TaxRateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (n: number | undefined) => void;
}) {
  const id = `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.01}
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? undefined : Math.min(100, Math.max(0, Number(v))));
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
    </div>
  );
}
